import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faSignOutAlt, faCog } from '@fortawesome/free-solid-svg-icons';
import { useAuthStore } from '@/store/auth';

interface UserDropdownMenuProps {
  variant?: 'default' | 'minimal';
}

export const UserDropdownMenu: React.FC<UserDropdownMenuProps> = ({ variant = 'default' }) => {
  const { user, logout } = useAuthStore();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // 处理点击外部关闭下拉菜单
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = () => {
    logout();
    setIsOpen(false);
  };

  if (!user) {
    return null;
  }

  const getDisplayName = () => {
    return user.full_name || user.username || '用户';
  };

  const getUserInitials = () => {
    const name = getDisplayName();
    return name.charAt(0).toUpperCase();
  };

  if (variant === 'minimal') {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="flex items-center space-x-2 px-4 py-2 rounded-full transition-all duration-200 bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 hover:border-white/30 shadow-lg hover:shadow-xl text-slate-100"
        >
          <div className="w-6 h-6 rounded-full bg-gradient-to-br from-primary-500 to-secondary-500 flex items-center justify-center shadow-md">
            <span className="text-xs font-medium text-white">{getUserInitials()}</span>
          </div>
          <span className="hidden md:inline text-sm font-medium">{getDisplayName()}</span>
        </button>
        
        {isOpen && (
          <div className="absolute right-0 mt-3 w-56 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl z-50 border border-white/20 overflow-hidden">
            <div className="py-2">
              <div className="px-4 py-3 border-b border-white/10">
                <p className="text-sm font-medium text-slate-50">{getDisplayName()}</p>
                <p className="text-xs text-slate-300 mt-0.5">{user.email}</p>
              </div>
              <Link 
                to="/profile" 
                className="flex items-center px-4 py-2.5 text-sm text-slate-100 hover:bg-white/10 transition-colors duration-150"
                onClick={() => setIsOpen(false)}
              >
                个人资料
              </Link>
              <Link 
                to="/my-data" 
                className="flex items-center px-4 py-2.5 text-sm text-slate-100 hover:bg-white/10 transition-colors duration-150"
                onClick={() => setIsOpen(false)}
              >
                我的数据中心
              </Link>
              <div className="border-t border-white/10 mt-1 pt-1">
                <button 
                  onClick={handleLogout}
                  className="w-full flex items-center px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors duration-150"
                >
                  退出登录
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center space-x-3 px-5 py-2.5 rounded-full transition-all duration-200 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white shadow-lg shadow-primary-500/30 hover:shadow-xl hover:shadow-primary-500/40 hover:scale-105"
      >
        <FontAwesomeIcon icon={faUser} className="text-sm" />
        <span className="font-medium">{getDisplayName()}</span>
      </button>
      
      {isOpen && (
        <div className="absolute right-0 mt-3 w-56 bg-white/10 backdrop-blur-xl rounded-2xl shadow-2xl z-50 border border-white/20 overflow-hidden">
          <div className="py-2">
            <div className="px-4 py-3 border-b border-white/10">
              <p className="text-sm font-medium text-slate-50">{getDisplayName()}</p>
              <p className="text-xs text-slate-300 mt-0.5">{user.email}</p>
            </div>
            <Link 
              to="/profile" 
              className="flex items-center px-4 py-2.5 text-sm text-slate-100 hover:bg-white/10 transition-colors duration-150"
              onClick={() => setIsOpen(false)}
            >
              个人资料
            </Link>
            <Link 
              to="/my-data" 
              className="flex items-center px-4 py-2.5 text-sm text-slate-100 hover:bg-white/10 transition-colors duration-150"
              onClick={() => setIsOpen(false)}
            >
              我的数据中心
            </Link>
            <div className="border-t border-white/10 mt-1 pt-1">
              <button 
                onClick={handleLogout}
                className="w-full flex items-center px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors duration-150"
              >
                退出登录
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserDropdownMenu;